import { useState } from 'react';
import { StationPartnerType } from '../../../models/Stations';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Autocomplete,
  MenuItem,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { pillButtonSx } from '../../../ui/styles';
import { useHistory } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { useToast, useCities } from '../../../hooks';
import { capitalize } from 'lodash';
import useStationMutations from '../../../hooks/useStationMutations';
import { useHasPermission } from '../../../helpers/api/auth/index';
const style = {
  fields: {
    padding: '32px 32px 32px 32px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    '.MuiTextField-root': {
      width: '250px',
    },
  },
};

const AddDialog = ({ onClose }) => {
  const history = useHistory();
  const t = useToast();
  const { createStation } = useStationMutations();

  const { cities } = useCities();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      address: '',
      operational: true,
      placeId: '',
      city_id: '',
      partnerType: '',
      lat: '',
      lon: '',
    },
  });
  const cityId = form.watch('city_id');
  const { isDirty, isValid, isSubmitting } = useFormState({ control: form.control });

  const onSubmit = async (data) => {
    const coordinates = [Number.parseFloat(data.lon), Number.parseFloat(data.lat)];
    const input = {
      name: data.name,
      address: data.address,
      operational: data.operational,
      placeId: data.placeId || null,
      city_id: data.city_id || null,
      partnerType: data.partnerType || null,
      coord: coordinates[0] && coordinates[1] ? coordinates : null,
    };
    const { success, station } = await createStation(input);
    if (success) {
      t.success('Station added');
      onClose();
      const params = new URLSearchParams(window.location.search);
      params.append('stationId', station.id);
      params.append('fullScreen', false);
      history.push({ search: params.toString() });
    } else {
      t.error('Station could not be added');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Add station</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box component="form" onSubmit={form.handleSubmit((data) => onSubmit(data))}>
        <Box sx={style.fields}>
          <TextField label="Name" control={form.control} name="name" />
          <TextField label="Address" control={form.control} name="address" />
          <TextField label="Partner Type" control={form.control} name="partnerType" select>
            {Object.keys(StationPartnerType).map((option) => (
              <MenuItem key={option} value={option}>
                {capitalize(option)}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            id="citySelector"
            label="City"
            placeholder="Filter by city"
            value={cities.find((c) => c.id === cityId) || null}
            sx={{ width: '250px' }}
            getOptionLabel={(c) => `${c.cityName}, ${c.stateAbbr}`}
            options={cities}
            onChange={(e, city) => {
              form.setValue('city_id', city.id, { shouldDirty: true, shouldTouch: true });
            }}
          />
          <TextField label="Place ID" control={form.control} name="placeId" />
          <TextField label="Lat" control={form.control} name="lat"></TextField>
          <TextField label="Lon" control={form.control} name="lon"></TextField>
          <Box padding="5px 24px" width="100%">
            <FormControlLabel
              sx={{ '.MuiFormControlLabel-label': { marginLeft: '24px' } }}
              control={<Switch sx={{ transform: 'scale(2)' }} name="operational" control={form.control} />}
              label="Operational"
            />
          </Box>
        </Box>
        <Button
          type="submit"
          disabled={!(isValid && isDirty)}
          loading={isSubmitting}
          variant="contained"
          size="large"
          disableElevation
          sx={{ width: '100%', padding: '20px', borderRadius: '0 0 4px 4px' }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
};

const AddStation = () => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const hasManageStationPermission = useHasPermission('station_mgmt');

  const openAddDialog = () => setShowAddDialog(true);
  const closeAddDialog = () => setShowAddDialog(false);

  if (!hasManageStationPermission) {
    return <></>;
  }

  return (
    <>
      <Button onClick={openAddDialog} sx={pillButtonSx}>
        + Add new station
      </Button>
      {showAddDialog && <AddDialog onClose={closeAddDialog} />}
    </>
  );
};

export default AddStation;
